"use client";

import React, { useEffect, useRef, useState } from "react";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const values = [{
  title: "Accompagnement complet de A à Z",
  description: "De la certification initiale à la conformité légale continue, nous vous guidons à chaque étape. Notre approche globale garantit la réussite de vos projets de formation et l'obtention pérenne de vos certifications RNCP/RS.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1606874/img/consulting_support_process.png"
}, {
  title: "Équipe de consultants expérimentés",
  description: "Notre équipe chevronnée maîtrise les subtilités du cadre juridique de la formation professionnelle. Nous vous assurons une navigation sereine et sécurisée face aux exigences complexes de France Compétences et Qualiopi.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1606874/img/legal_expertise_team.png"
}, {
  title: "Programmes de formation sur mesure",
  description: "Nous ne croyons pas aux solutions standardisées. Chaque accompagnement est pensé spécifiquement pour répondre aux enjeux uniques de votre organisation, valorisant ainsi votre ingénierie pédagogique et votre identité.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1606874/img/custom_training_solutions.png"
}];
function ValueBlock({
  item,
  index,
  "data-index": dataIndex
}: {
  item: typeof values[0];
  index: number;
  "data-index": number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const isEven = index % 2 === 0;
  return <div ref={ref} data-index={dataIndex} className={`flex flex-col md:flex-row items-center gap-12 lg:gap-24 transition-all duration-1000 ease-out ${isEven ? "md:flex-row" : "md:flex-row-reverse"} ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-16"}`}>
      {}
      <div className="w-full md:w-1/2 relative z-10">
        <div className="bg-background/95 backdrop-blur-md p-8 lg:p-12 rounded-2xl shadow-xl border border-border/50 relative">
          {}
          <div className={`hidden md:block absolute top-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-accent border-4 border-background shadow-sm z-20 ${isEven ? "-right-2 translate-x-1/2" : "-left-2 -translate-x-1/2"}`} />
          
          <h3 className="text-3xl lg:text-4xl font-serif text-foreground font-bold mb-6 leading-tight">
            {item.title}
          </h3>
          <p className="text-lg text-foreground/80 font-sans leading-relaxed">
            {item.description}
          </p>
        </div>
      </div>

      {}
      <div className="w-full md:w-1/2 relative z-10">
        <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-2xl relative group">
          <img src={item.image} alt={item.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
          {}
          <div className="absolute inset-0 bg-gradient-to-tr from-primary/20 to-transparent mix-blend-overlay pointer-events-none"></div>
        </div>
        {}
        <div className={`absolute -z-10 w-full h-full rounded-2xl bg-accent/5 top-6 ${isEven ? "-right-6" : "-left-6"} transition-transform duration-700 group-hover:translate-x-2 group-hover:translate-y-2`}></div>
      </div>
    </div>;
}
export default function WhyChooseUs() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.5
  });
  return;
}